/**
 * Revision 하나의 매핑 편집 상태 — 저장본, 편집본, 고른 손 · 역할, 저장 · 삭제 요청.
 *
 * 점검 세션은 episode 하나의 것이다. 매핑은 Revision 하나의 것이다. 그래서 편집 상태를 세션에 두면
 * episode 를 바꿀 때 편집본을 잃는다. 이 객체는 Revision별로 보존한다. 그 Dataset 을 연 상세가 모두 놓으면
 * (`holdInspectDataset`) 함께 버린다.
 *
 * 같은 Revision 을 여러 화면(창 · route 페이지)이 함께 볼 수 있다. 보이는 화면은 `watch()` 로 센다. 한 화면이라도
 * 보이면 서버를 따르고 POC 를 폴링한다.
 *
 * 저장본이 확인되기 전에는 편집하지 않는다. 기준이 없으면 "저장하지 않은 변경" 을 말할 수 없다.
 *
 * 서버 저장본이 정본이다. 다른 창이나 MCP 클라이언트가 저장본을 바꾸면 편집본과 적용하지 않은 JSON 편집을
 * 버리고 서버 값을 따른다. 변경 알림 · 화면 복귀 · 주기 대조가 저장본을 다시 받고, 저장하기 전에도 다시 받아
 * digest 를 대조한다. 그사이 저장본이 바뀌었으면 PUT 하지 않고 서버 값을 따른다. 대조와 PUT 사이에 끼는
 * 저장은 막지 못한다. BE 에 조건부 PUT 이 없어서다.
 *
 * 편집 방식은 폼과 JSON 둘이다. 둘 다 같은 편집본을 바꾼다. JSON 텍스트는 `적용` 하고 스키마를 통과할 때만
 * 편집본이 된다. 적용하지 않은 텍스트가 있는 동안에는 폼 편집 · 저장 · 검증 · POC 를 하지 않는다. 보내는
 * 매핑과 화면의 텍스트가 다르면 사람이 무엇을 보냈는지 알 수 없다. 같은 열을 두 번 고른 칸이 있는 동안에도
 * 저장 · 검증 · POC 를 하지 않는다(`canSendDraft`).
 *
 * Owns:
 * - 저장본 받기와 그 상태, 편집본과 저장하지 않은 변경, 고른 손 · 역할, 편집 방식과 적용하지 않은 JSON 텍스트,
 *   서버 저장본의 관측과 따르기(그 알림), 저장 전 digest 대조, 저장 · 삭제 요청과 그 실패 문구,
 *   검증 요청과 결과 · 보낸 매핑 사본, POC 접수와 폴링(화면이 보일 때만), 늦은 응답의 무시.
 *
 * Does not own:
 * - 편집 규칙 — `mappingEdit.ts`. JSON 텍스트 형식과 문제 — `mappingJson.ts`. 요청과 응답 읽기 —
 *   `api/mappingApi.ts`. 그리기 — `page/`.
 */
import { toast } from "@carbon6/c6-frontend-design";
import { isHttpClientError } from "@/shared/http";
import { Holders } from "@/shared/utils/holders";
import { isPageHidden } from "@/shared/utils/pageVisibility";
import {
  deleteRevisionMapping,
  getPoc,
  getRevisionMapping,
  parseErrorIssues,
  requestPoc,
  reviewMapping,
  saveRevisionMapping,
  validateMapping,
} from "../api/mappingApi";
import {
  emptyMapping,
  type HandRoleName,
  isPocFinished,
  type Mapping,
  type MappingIssue,
  type MappingValidation,
  type MappingReview,
  type PocJob,
  repeatedColumnPaths,
} from "../domain/mapping";
import type { StoredMapping } from "../domain/storedMapping";
import {
  resolveMappingDeleteMessage,
  resolveMappingLoadMessage,
  resolveMappingSaveMessage,
  resolveMappingValidateMessage,
  resolvePocPollMessage,
  resolvePocRequestMessage,
} from "./inspectErrorMessage";
import { PocPoller } from "./pocPoller";
import { sameMapping } from "./mappingEdit";
import { formatMappingJson, readMappingJson } from "./mappingJson";
import { mappingChanges } from "./mappingChanges";
import type { RoleField } from "./issuePath";

export type MappingField = RoleField | "hands" | "cameras" | "time";

/**
 * revision 에 저장된 매핑의 상태.
 *
 * `no_revision` 은 Probe 에 revision 이 없어 물을 곳이 없다는 뜻이다. `none` 은 물었지만 저장된
 * 매핑이 없다는 뜻이다. 두 상태를 합치지 않는다(규칙 §6).
 * `failed.unreadable` 은 저장본은 있지만 지금의 매핑 형식으로 읽지 못한다는 뜻이다(GET 422 `mapping.invalid`).
 * 다시 불러와도 같으므로 저장을 지우고 새로 시작하는 길을 준다.
 */
export type StoredMappingState =
  | { readonly status: "loading" }
  | { readonly status: "no_revision" }
  | { readonly status: "none" }
  | { readonly status: "saved"; readonly stored: StoredMapping }
  | { readonly status: "failed"; readonly message: string; readonly unreadable: boolean };

/** 검증 범위 — 보고 있는 episode 하나, 또는 Probe 샘플의 episode 전부. */
export type ValidationScope = "episode" | "sample";

/**
 * 마지막 검증. `sent` 는 검증에 보낸 매핑의 사본이다. FE 는 digest 를 계산하지 않는다. 그래서 결과가
 * 지금 편집본의 것인지는 이 사본과 편집본을 비교해 정한다.
 */
export type ValidationState =
  | { readonly status: "idle" }
  | { readonly status: "running"; readonly scope: ValidationScope }
  | {
      readonly status: "done";
      readonly scope: ValidationScope;
      readonly result: MappingValidation;
      readonly sent: Mapping;
    }
  | { readonly status: "failed"; readonly scope: ValidationScope; readonly message: string };

/**
 * 이 화면에서 접수한 POC. BE 에 목록 API 가 없어 새로고침하면 사라진다. `sent` 는 접수할 때 보낸 매핑의
 * 사본이다. 결과가 지금 편집본의 것인지는 이 사본으로 정한다.
 */
export type PocTracking =
  | { readonly status: "idle" }
  | { readonly status: "requesting" }
  | {
      readonly status: "tracking";
      readonly job: PocJob;
      readonly sent: Mapping;
      /** 마지막 폴링 실패. 다음 응답이 지운다. */
      readonly pollError: string | null;
    }
  | { readonly status: "failed"; readonly message: string; readonly detail: string | null };

/** 물어서 확인한 서버 저장본. 저장본이 없는 것도 확인한 값이다. */
type KnownMappingState = Extract<StoredMappingState, { status: "saved" | "none" }>;

/** 편집 방식. 두 방식 모두 같은 편집본을 바꾼다. */
export type MappingEditMode = "form" | "json";

/** 폼이 보고 있는 손 하나와 역할. */
export type MappingFocus = {
  readonly handIndex: number;
  readonly role: HandRoleName;
};

export type ReviewState =
  | { readonly status: "idle" }
  | { readonly status: "running" }
  | { readonly status: "done"; readonly result: MappingReview }
  | { readonly status: "failed"; readonly message: string };

export type MappingEditorState = {
  readonly saved: StoredMappingState;
  readonly refreshing: boolean;
  readonly refreshError: string | null;
  readonly changePending: boolean;
  /** 편집 중인 매핑. 저장본이 없으면 빈 매핑에서 시작한다. */
  readonly draft: Mapping;
  /** 편집본이 저장본(없으면 빈 매핑)과 다른지. */
  readonly dirty: boolean;
  readonly focus: MappingFocus;
  readonly field: MappingField;
  readonly mode: MappingEditMode;
  /**
   * 적용하지 않은 JSON 텍스트. `null` 이면 적용하지 않은 편집이 없다. 그때 JSON 탭은 편집본을 직렬화해 보인다.
   * `null` 이 아니면 편집 방식은 `json` 이다.
   */
  readonly jsonText: string | null;
  readonly pending: "save" | "delete" | null;
  /** 마지막 저장 · 삭제의 실패 문구. 다음 요청이나 되돌리기가 지운다. */
  readonly commandError: string | null;
  /**
   * 마지막 일반 저장을 막은 자리(422 `mapping.invalid` 의 `issues`)와 그때 보낸 매핑. `commandError` 와 함께 서고
   * 함께 지워진다. issue 경로는 보낸 매핑의 것이다. 편집본이 그 뒤에 바뀌면 화면은 지난 결과로 보인다(검증 결과와
   * 같은 규칙). 오류 본문에 issue 가 없거나 모양이 틀리면 `null` 이다. 그때는 문구만 보인다.
   */
  readonly saveIssues: SaveIssues | null;
  readonly validation: ValidationState;
  readonly poc: PocTracking;
  readonly review: ReviewState;
};

export type SaveIssues = {
  readonly sent: Mapping;
  readonly issues: readonly MappingIssue[];
};

export type SaveOutcome = {
  /** 첫 저장이면 `true`. */
  readonly created: boolean;
};

export type MappingEditorDeps = {
  fetchMapping?: typeof getRevisionMapping;
  saveMapping?: typeof saveRevisionMapping;
  deleteMapping?: typeof deleteRevisionMapping;
  validateMapping?: typeof validateMapping;
  requestPoc?: typeof requestPoc;
  reviewMapping?: typeof reviewMapping;
  getPoc?: typeof getPoc;
};

const FIRST_FOCUS: MappingFocus = { handIndex: 0, role: "observation" };

function baselineOf(saved: StoredMappingState): Mapping {
  return saved.status === "saved" ? saved.stored.mapping : emptyMapping();
}

/** 두 저장본이 같은지. digest 와 `isTemp` 로 정한다. 둘 다 저장본이 없어도 같다. */
function sameStored(left: StoredMappingState, right: StoredMappingState): boolean {
  if (left.status === "none" && right.status === "none") return true;
  return (
    left.status === "saved" &&
    right.status === "saved" &&
    left.stored.digest === right.stored.digest &&
    left.stored.isTemp === right.stored.isTemp
  );
}

/** 일반 저장을 막은 자리. 422 `mapping.invalid` 본문의 `issues` 다. 본문 모양이 틀리면 문구만 보이므로 `null` 이다. */
function saveIssuesOf(error: unknown, sent: Mapping): SaveIssues | null {
  if (!isHttpClientError(error) || error.code !== "mapping.invalid") {
    return null;
  }
  try {
    const issues = parseErrorIssues(error.details);
    return issues === null || issues.length === 0 ? null : { sent, issues };
  } catch {
    return null;
  }
}

/** 저장본은 있지만 지금의 매핑 형식으로 읽지 못한다(GET 422 `mapping.invalid`). */
function isUnreadable(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "mapping.invalid";
}

type FailedMappingState = Extract<StoredMappingState, { status: "failed" }>;

function failedOf(error: unknown): FailedMappingState {
  return {
    status: "failed",
    message: resolveMappingLoadMessage(error),
    unreadable: isUnreadable(error),
  };
}

function knownOf(stored: StoredMapping | null): KnownMappingState {
  return stored === null ? { status: "none" } : { status: "saved", stored };
}

/** 보던 손이 새 매핑에 없으면 첫 손으로 옮긴다. */
function focusWithin(focus: MappingFocus, draft: Mapping): MappingFocus {
  return focus.handIndex < draft.hands.length ? focus : { ...focus, handIndex: 0 };
}

/** 서버 값을 따른 계기. `save` 는 저장 전 대조에서 알았고 그 저장은 보내지 않았다. */
type FollowReason = "observed" | "save";

function followMessage(
  server: KnownMappingState | FailedMappingState,
  reason: FollowReason,
  discarded: boolean,
): string {
  if (server.status === "failed") {
    const change = "서버의 매핑을 지금의 매핑 형식으로 읽지 못합니다.";
    if (reason === "save") return `${change} 저장하지 않았습니다.`;
    return discarded ? `${change} 저장하지 않은 편집은 버렸습니다.` : change;
  }
  const deleted = server.status === "none";
  if (reason === "save") {
    return deleted
      ? "서버의 매핑이 먼저 삭제되어 저장하지 않았습니다."
      : "서버 매핑이 먼저 바뀌어 저장하지 않았습니다. 서버 매핑을 다시 불러왔습니다.";
  }
  const change = deleted
    ? "서버의 매핑이 삭제되었습니다."
    : "서버 매핑이 바뀌어 다시 불러왔습니다.";
  return discarded ? `${change} 저장하지 않은 편집은 버렸습니다.` : change;
}

/** 저장본을 알 때만 편집 · 저장한다. */
export function isEditable(state: MappingEditorState): boolean {
  return state.saved.status === "saved" || state.saved.status === "none";
}

/** 적용하지 않은 JSON 편집이 있는지. 있으면 저장 · 검증 · POC 를 하지 않는다. */
export function hasUnappliedJson(state: MappingEditorState): boolean {
  return state.jsonText !== null;
}

/**
 * 편집본을 서버에 보낼 수 있는지. 저장 · 검증 · POC 가 이 조건을 같이 쓴다. 적용하지 않은 JSON 편집이 있거나
 * 같은 열을 두 번 고른 칸이 있으면 보내지 않는다. BE 는 중복 열을 422 로 거부한다.
 */
export function canSendDraft(state: MappingEditorState): boolean {
  return (
    isEditable(state) && !hasUnappliedJson(state) && repeatedColumnPaths(state.draft).length === 0
  );
}

/** 현재 매핑과 Probe의 샘플 전체를 통과한 서버 검증만 사용한다. */
export function currentPocValidation(
  state: MappingEditorState,
  probeUuid: string,
): MappingValidation | null {
  const checked = state.validation;
  if (
    !canSendDraft(state) ||
    checked.status !== "done" ||
    checked.scope !== "sample" ||
    !sameMapping(checked.sent, state.draft) ||
    checked.result.probeUuid !== probeUuid
  )
    return null;
  const result = checked.result;
  if (
    result.episodes.length === 0 ||
    result.episodes.some((item) => !item.valid) ||
    result.tcpAssessments.length === 0 ||
    result.tcpAssessments.some((item) => item.status === "blocked" || item.issues.length > 0)
  )
    return null;
  return result;
}

export function pocNeedsReview(result: MappingValidation): boolean {
  return result.tcpAssessments.some((item) => item.status === "requires_experimental_review");
}

export function canRequestPoc(state: MappingEditorState, probeUuid: string): boolean {
  const result = currentPocValidation(state, probeUuid);
  return (
    result !== null &&
    (!pocNeedsReview(result) ||
      (state.review.status === "done" &&
        state.review.result.validationUuid === result.validationUuid))
  );
}

/** 버리면 잃는 편집이 있는지 — 저장하지 않은 변경 또는 적용하지 않은 JSON 편집. */
export function hasUnsavedEdits(state: MappingEditorState): boolean {
  return state.dirty || hasUnappliedJson(state);
}

export class MappingEditor {
  readonly revisionUuid: string | null;
  private state: MappingEditorState;
  private readonly listeners = new Set<() => void>();
  private readonly deps: Required<MappingEditorDeps>;
  private abort: AbortController | null = null;
  private generation = 0;
  private validationGeneration = 0;
  private disposed = false;
  /** 보이는 화면이 하나라도 있다. `viewers` 가 처음 잡힐 때 켜고 마지막으로 놓일 때 끈다. */
  private active = false;
  private readonly viewers = new Holders<void>({
    first: () => this.setActive(true),
    last: () => this.setActive(false),
  });
  private readonly poller: PocPoller;
  private observationAbort: AbortController | null = null;
  private observationGeneration = 0;
  private observationRequest: Promise<void> | null = null;
  private observeAgain = false;
  private reconcileTimer: ReturnType<typeof setInterval> | null = null;
  private unsubscribeChanges: (() => void) | null = null;

  constructor(revisionUuid: string | null, deps: MappingEditorDeps = {}) {
    this.revisionUuid = revisionUuid;
    this.deps = {
      fetchMapping: deps.fetchMapping ?? getRevisionMapping,
      saveMapping: deps.saveMapping ?? saveRevisionMapping,
      deleteMapping: deps.deleteMapping ?? deleteRevisionMapping,
      validateMapping: deps.validateMapping ?? validateMapping,
      requestPoc: deps.requestPoc ?? requestPoc,
      reviewMapping: deps.reviewMapping ?? reviewMapping,
      getPoc: deps.getPoc ?? getPoc,
    };
    this.poller = new PocPoller({
      fetchJob: (jobUuid) => this.deps.getPoc(this.revisionUuid ?? "", jobUuid),
      onJob: (job) => this.onPocJob(job),
      onError: (error) => this.onPocError(error),
    });
    this.state = {
      saved: revisionUuid === null ? { status: "no_revision" } : { status: "loading" },
      refreshing: false,
      refreshError: null,
      changePending: false,
      draft: emptyMapping(),
      dirty: false,
      focus: FIRST_FOCUS,
      field: "orientation",
      mode: "form",
      jsonText: null,
      pending: null,
      commandError: null,
      saveIssues: null,
      validation: { status: "idle" },
      poc: { status: "idle" },
      review: { status: "idle" },
    };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): MappingEditorState => this.state;

  init(datasetUuid: string | null): void {
    if (this.disposed || this.unsubscribeChanges !== null) return;
    this.unsubscribeChanges = mappingChanges.subscribe((change) => {
      // 조사 사실의 변경은 매핑 저장본과 관계없다.
      if (change.kind === "facts_changed") return;
      if (
        change.kind === "changed" &&
        (change.revisionUuid !== this.revisionUuid || change.datasetUuid !== datasetUuid)
      )
        return;
      if (this.state.saved.status === "loading" || this.state.saved.status === "failed") {
        void this.load();
      } else if (change.kind !== "offline") {
        this.invalidateServer();
      }
    });
    if (!mappingChanges.connecting) void this.load();
  }

  /** 비활성 창에는 변경만 표시하고, 활성 창만 최신 저장본을 확인한다. */
  invalidateServer(): void {
    this.patch({ changePending: true });
    if (this.active && !isPageHidden()) void this.refreshServer();
  }

  /** 연속 알림은 합치되 조회 중 새 알림이 오면 완료 뒤 다시 확인한다. */
  refreshServer(): Promise<void> {
    this.observeAgain = true;
    if (this.observationRequest !== null) return this.observationRequest;
    this.observationRequest = this.observeServer().finally(() => {
      this.observationRequest = null;
    });
    return this.observationRequest;
  }

  private async observeServer(): Promise<void> {
    let queried = false;
    const startedActive = this.active;
    while (this.observeAgain && !this.disposed && this.revisionUuid !== null) {
      if (queried && startedActive && (!this.active || isPageHidden())) return;
      if (this.state.pending !== null || !isEditable(this.state)) return;
      queried = true;
      this.observeAgain = false;
      const generation = this.observationGeneration;
      const abort = new AbortController();
      this.observationAbort = abort;
      this.patch({ refreshing: true, refreshError: null });
      try {
        const server = knownOf(await this.deps.fetchMapping(this.revisionUuid, abort.signal));
        if (this.disposed || generation !== this.observationGeneration) continue;
        this.patch({ refreshing: false, changePending: this.observeAgain });
        if (!sameStored(this.state.saved, server)) this.follow(server, "observed");
      } catch (error) {
        if (this.disposed || generation !== this.observationGeneration) continue;
        if (isUnreadable(error)) {
          // 저장본이 읽지 못하는 모양으로 바뀌었다. 다른 변경처럼 서버를 따른다. 실패 화면이 `저장 삭제` 를 준다.
          this.patch({ refreshing: false });
          this.follow(failedOf(error), "observed");
          continue;
        }
        this.patch({ refreshing: false, refreshError: resolveMappingLoadMessage(error) });
      }
    }
  }

  private cancelObservation(): void {
    this.observationGeneration += 1;
    this.observationAbort?.abort();
    this.patch({ refreshing: false });
  }

  /**
   * 다른 창이나 MCP 클라이언트가 바꾼 서버 저장본을 따른다. 편집본과 적용하지 않은 JSON 편집은 버리고 보던
   * 자리는 둔다. 화면이 말없이 바뀌지 않게 알린다.
   */
  private follow(server: KnownMappingState | FailedMappingState, reason: FollowReason): void {
    const discarded = hasUnsavedEdits(this.state);
    this.adopt(server, { keepPlace: true });
    const message = followMessage(server, reason, discarded);
    if (reason === "save" || discarded) {
      toast.warning(message);
    } else {
      toast.info(message);
    }
  }

  /**
   * 저장본을 (다시) 받는다. 받으면 편집본을 저장본으로 맞춘다. 저장 · 삭제가 도는 동안에는 읽지 않는다. 그 요청이
   * 끝난 뒤의 저장본을 모르는 결과가 요청의 결과를 덮기 때문이다. 요청은 끝나면 서버를 다시 확인한다.
   */
  async load(): Promise<void> {
    if (this.revisionUuid === null || this.disposed || this.state.pending !== null) {
      return;
    }
    this.abort?.abort();
    this.cancelObservation();
    const abort = new AbortController();
    this.abort = abort;
    const generation = ++this.generation;
    this.patch({ saved: { status: "loading" } });
    let saved: StoredMappingState;
    try {
      saved = knownOf(await this.deps.fetchMapping(this.revisionUuid, abort.signal));
    } catch (error) {
      saved = failedOf(error);
    }
    if (this.disposed || generation !== this.generation) {
      return;
    }
    this.adopt(saved);
  }

  /**
   * 받은 저장본을 기준으로 삼고 편집본을 그 값으로 맞춘다. 적용하지 않은 JSON 편집과 지난 검증 결과는
   * 버린다. 편집 방식은 그대로 둔다.
   *
   * `keepPlace` 는 저장본이 다른 저장본으로 바뀔 때 보던 손 · 역할 · 필드를 둔다. 저장본이 새로 생기거나
   * 사라지거나 손이 없으면 첫 화면이 달라지므로 처음 자리로 간다.
   */
  private adopt(saved: StoredMappingState, { keepPlace = false } = {}): void {
    this.validationGeneration += 1;
    const draft = baselineOf(saved);
    const stay =
      keepPlace &&
      this.state.saved.status === "saved" &&
      saved.status === "saved" &&
      draft.hands.length > 0;
    this.patch({
      saved,
      changePending: false,
      refreshError: null,
      draft,
      dirty: false,
      focus: stay ? focusWithin(this.state.focus, draft) : FIRST_FOCUS,
      // 새 매핑은 MCP·직접 작성 선택을, 저장된 빈 구성은 손 선택을 먼저 보여 준다.
      field: stay
        ? this.state.field
        : saved.status === "saved" && draft.hands.length === 0
          ? "hands"
          : "orientation",
      jsonText: null,
      commandError: null,
      saveIssues: null,
      validation: { status: "idle" },
    });
  }

  /**
   * 폼 편집으로 편집본을 바꾼다. 저장본을 모르거나 적용하지 않은 JSON 편집이 있으면 무시한다. 무시하지 않으면
   * JSON 텍스트가 가리키는 편집본이 사라진다.
   */
  edit(change: (draft: Mapping) => Mapping): void {
    if (!isEditable(this.state) || hasUnappliedJson(this.state)) {
      return;
    }
    this.replaceDraft(change(this.state.draft), {});
  }

  private replaceDraft(draft: Mapping, extra: Partial<MappingEditorState>): void {
    this.patch({
      draft,
      dirty: !sameMapping(draft, baselineOf(this.state.saved)),
      focus: focusWithin(this.state.focus, draft),
      ...extra,
    });
  }

  /** 편집 방식을 바꾼다. 적용하지 않은 JSON 편집이 있으면 폼으로 가지 않는다. 먼저 적용하거나 버려야 한다. */
  setMode(mode: MappingEditMode): void {
    if (mode === "form" && hasUnappliedJson(this.state)) {
      return;
    }
    this.patch({ mode });
  }

  /** JSON 탭의 텍스트를 받는다. 편집본을 직렬화한 텍스트와 같으면 적용하지 않은 편집이 없다. */
  setJsonText(text: string): void {
    if (!isEditable(this.state) || this.state.mode !== "json") {
      return;
    }
    this.patch({ jsonText: text === formatMappingJson(this.state.draft) ? null : text });
  }

  /** JSON 텍스트가 문법과 스키마를 통과하면 편집본으로 삼는다. 통과하지 못하면 아무것도 바꾸지 않는다. */
  applyJson(): boolean {
    const { jsonText } = this.state;
    if (jsonText === null || !isEditable(this.state)) {
      return false;
    }
    const read = readMappingJson(jsonText);
    if (!read.ok) {
      return false;
    }
    this.replaceDraft(read.mapping, { jsonText: null });
    return true;
  }

  /** 적용하지 않은 JSON 편집을 버린다. 편집본은 그대로다. */
  discardJson(): void {
    this.patch({ jsonText: null });
  }

  setFocus(focus: MappingFocus): void {
    this.patch({ focus });
  }

  setField(field: MappingField): void {
    this.patch({ field });
  }

  /** 편집본과 적용하지 않은 JSON 편집을 버리고 저장본(없으면 빈 매핑)으로 돌아간다. */
  revert(): void {
    if (!isEditable(this.state)) {
      return;
    }
    this.patch({
      draft: baselineOf(this.state.saved),
      dirty: false,
      jsonText: null,
      commandError: null,
      saveIssues: null,
    });
  }

  /**
   * 편집본을 저장한다. `isTemp` 는 사람이 고른 표시다. 실패하면 `null` 이고 문구는 상태에 남는다.
   *
   * 먼저 서버 저장본을 다시 받는다. digest · `isTemp` · 저장 유무가 이 화면이 아는 저장본과 다르면 PUT 하지
   * 않고 서버 값을 따른다. 결과는 `null` 이다.
   */
  async save(isTemp: boolean): Promise<SaveOutcome | null> {
    const revisionUuid = this.revisionUuid;
    if (revisionUuid === null || !canSendDraft(this.state) || this.state.pending !== null) {
      return null;
    }
    const known = this.state.saved;
    this.cancelObservation();
    this.patch({ pending: "save", commandError: null, saveIssues: null });
    let server: KnownMappingState;
    try {
      server = knownOf(await this.deps.fetchMapping(revisionUuid));
    } catch (error) {
      if (this.disposed) {
        return null;
      }
      this.patch({ pending: null });
      if (isUnreadable(error)) {
        // 저장본이 이 화면이 아는 것과 다르다. 다른 변경처럼 서버를 따르고 보내지 않는다.
        this.follow(failedOf(error), "save");
      } else {
        this.patch({ commandError: resolveMappingSaveMessage(error) });
      }
      return null;
    }
    if (this.disposed) {
      return null;
    }
    if (!sameStored(server, known)) {
      this.patch({ pending: null });
      this.follow(server, "save");
      return null;
    }
    return this.put(revisionUuid, isTemp);
  }

  /** 편집본을 그대로 PUT 한다. 서버는 받은 값을 보관하므로 저장 뒤 편집본은 저장본과 같다. */
  private async put(revisionUuid: string, isTemp: boolean): Promise<SaveOutcome | null> {
    const draft = this.state.draft;
    try {
      const { stored, created } = await this.deps.saveMapping(revisionUuid, draft, isTemp);
      if (this.disposed) {
        return null;
      }
      this.patch({
        saved: { status: "saved", stored },
        changePending: false,
        pending: null,
        // 요청 중에 편집이 더 있었을 수 있다. 지금 편집본을 새 저장본과 비교한다.
        dirty: !sameMapping(this.state.draft, stored.mapping),
      });
      if (this.active) void this.refreshServer();
      return { created };
    } catch (error) {
      if (!this.disposed) {
        this.patch({
          pending: null,
          commandError: resolveMappingSaveMessage(error),
          saveIssues: saveIssuesOf(error, draft),
        });
      }
      return null;
    }
  }

  /** 저장된 매핑을 지운다. 편집본은 화면에 남는다. 성공하면 `true` 다. */
  async remove(): Promise<boolean> {
    if (
      this.revisionUuid === null ||
      this.state.saved.status !== "saved" ||
      this.state.pending !== null
    ) {
      return false;
    }
    this.cancelObservation();
    this.patch({ pending: "delete", commandError: null, saveIssues: null });
    try {
      await this.deps.deleteMapping(this.revisionUuid);
      if (this.disposed) {
        return false;
      }
      this.patch({
        saved: { status: "none" },
        changePending: false,
        pending: null,
        dirty: !sameMapping(this.state.draft, emptyMapping()),
      });
      if (this.active) void this.refreshServer();
      return true;
    } catch (error) {
      if (!this.disposed) {
        this.patch({ pending: null, commandError: resolveMappingDeleteMessage(error) });
      }
      return false;
    }
  }

  /**
   * 읽지 못하는 저장본을 지우고 빈 매핑에서 새로 시작한다. 성공하면 `true` 다. 실패하면 `commandError` 에 남는다.
   * `remove` 와 따로 둔다. `remove` 는 읽은 저장본만 지우고 편집본을 남기지만, 여기에는 남길 편집본이 없다.
   */
  async discardUnreadable(): Promise<boolean> {
    const { saved } = this.state;
    if (
      this.revisionUuid === null ||
      saved.status !== "failed" ||
      !saved.unreadable ||
      this.state.pending !== null
    ) {
      return false;
    }
    // 진행 중인 읽기 결과가 지운 뒤의 상태를 덮지 않게 한다. 요청이 도는 동안 `load` 는 시작하지 않는다.
    this.abort?.abort();
    this.generation += 1;
    this.cancelObservation();
    this.patch({ pending: "delete", commandError: null, saveIssues: null });
    try {
      await this.deps.deleteMapping(this.revisionUuid);
      if (this.disposed) {
        return false;
      }
      this.patch({ pending: null });
      this.adopt({ status: "none" });
      if (this.active) void this.refreshServer();
      return true;
    } catch (error) {
      if (!this.disposed) {
        this.patch({ pending: null, commandError: resolveMappingDeleteMessage(error) });
      }
      return false;
    }
  }

  /**
   * 편집본을 검증한다. 저장 · `isTemp` 는 바뀌지 않는다. 결과가 오기 전에 편집해도 결과는 보낸 매핑의
   * 것으로 남는다.
   */
  async validate(request: {
    probeUuid: string;
    sourceIds: readonly string[];
    scope: ValidationScope;
  }): Promise<void> {
    if (
      this.revisionUuid === null ||
      !canSendDraft(this.state) ||
      this.state.validation.status === "running" ||
      request.sourceIds.length === 0
    ) {
      return;
    }
    const sent = this.state.draft;
    const generation = ++this.validationGeneration;
    this.patch({ validation: { status: "running", scope: request.scope } });
    try {
      const result = await this.deps.validateMapping(this.revisionUuid, {
        mapping: sent,
        probeUuid: request.probeUuid,
        sourceIds: request.sourceIds,
      });
      if (!this.disposed && generation === this.validationGeneration) {
        this.patch({ validation: { status: "done", scope: request.scope, result, sent } });
      }
    } catch (error) {
      if (!this.disposed && generation === this.validationGeneration) {
        this.patch({
          validation: {
            status: "failed",
            scope: request.scope,
            message: resolveMappingValidateMessage(error),
          },
        });
      }
    }
  }

  /** 명시적으로 누른 검토 기록 버튼만 서버 기록을 만든다. */
  async reviewCandidate(probeUuid: string, note: string): Promise<void> {
    const checked = currentPocValidation(this.state, probeUuid);
    if (
      this.revisionUuid === null ||
      checked === null ||
      !pocNeedsReview(checked) ||
      this.state.review.status === "running" ||
      note.trim() === ""
    )
      return;
    this.patch({ review: { status: "running" } });
    try {
      const result = await this.deps.reviewMapping(this.revisionUuid, {
        mapping: this.state.draft,
        probeUuid,
        validationUuid: checked.validationUuid,
        note,
      });
      if (!this.disposed) this.patch({ review: { status: "done", result } });
    } catch (error) {
      if (!this.disposed)
        this.patch({ review: { status: "failed", message: resolveMappingValidateMessage(error) } });
    }
  }

  /**
   * 편집본으로 POC 를 접수한다. 저장된 매핑이 아니다. 끝나지 않은 POC 가 있으면 새로 접수하지 않는다.
   * 접수 뒤에는 화면이 보이는 동안 5초마다 상태를 묻는다.
   */
  async requestPoc(probeUuid: string): Promise<void> {
    const { poc } = this.state;
    if (
      this.revisionUuid === null ||
      !canSendDraft(this.state) ||
      !canRequestPoc(this.state, probeUuid) ||
      poc.status === "requesting" ||
      (poc.status === "tracking" && !isPocFinished(poc.job.state))
    ) {
      return;
    }
    const sent = this.state.draft;
    const validation = currentPocValidation(this.state, probeUuid);
    if (validation === null) return;
    const reviewUuid =
      pocNeedsReview(validation) && this.state.review.status === "done"
        ? this.state.review.result.uuid
        : null;
    this.poller.stop();
    this.patch({ poc: { status: "requesting" } });
    try {
      const job = await this.deps.requestPoc(this.revisionUuid, {
        probeUuid,
        mapping: sent,
        validationUuid: validation.validationUuid,
        reviewUuid,
      });
      if (this.disposed) {
        return;
      }
      this.patch({ poc: { status: "tracking", job, sent, pollError: null } });
      this.followPoc(false);
    } catch (error) {
      if (!this.disposed) {
        this.patch({
          poc: {
            status: "failed",
            message: resolvePocRequestMessage(error),
            detail: isHttpClientError(error) ? error.serverMessage : null,
          },
        });
      }
    }
  }

  /**
   * 화면 하나가 이 편집 상태를 보기 시작한다. 돌려준 함수를 부르면 그만 본다. 보는 화면이 하나라도 있으면 활성이다 —
   * 같은 Revision 을 보는 창 하나를 최소화해도 보이는 다른 화면은 계속 서버를 따른다.
   */
  watch(): () => void {
    return this.viewers.hold();
  }

  /** 보이는 화면이 없으면 POC 폴링을 멈추고, 다시 생기면 곧바로 묻는다(규칙 §8). */
  private setActive(active: boolean): void {
    if (this.active === active) {
      return;
    }
    this.active = active;
    if (active) {
      this.followPoc(true);
      if (isEditable(this.state)) void this.refreshServer();
      this.reconcileTimer = setInterval(() => this.invalidateServer(), 30_000);
    } else {
      this.poller.stop();
      if (this.reconcileTimer !== null) clearInterval(this.reconcileTimer);
      this.reconcileTimer = null;
    }
  }

  private followPoc(immediate: boolean): void {
    const { poc } = this.state;
    if (this.active && poc.status === "tracking" && !isPocFinished(poc.job.state)) {
      this.poller.start(poc.job.uuid, { immediate });
    }
  }

  private onPocJob(job: PocJob): void {
    const { poc } = this.state;
    if (poc.status === "tracking" && poc.job.uuid === job.uuid) {
      this.patch({ poc: { ...poc, job, pollError: null } });
    }
  }

  private onPocError(error: unknown): void {
    const { poc } = this.state;
    if (poc.status === "tracking") {
      this.patch({ poc: { ...poc, pollError: resolvePocPollMessage(error) } });
    }
  }

  dispose(): void {
    this.disposed = true;
    this.setActive(false);
    this.viewers.clear();
    this.unsubscribeChanges?.();
    this.unsubscribeChanges = null;
    this.observationAbort?.abort();
    this.poller.dispose();
    this.abort?.abort();
    this.listeners.clear();
  }

  private patch(next: Partial<MappingEditorState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// 서로 다른 창의 편집본을 보존하고 Dataset 창을 닫을 때 해당 항목만 해제한다.
const editors = new Map<string | null, { editor: MappingEditor; datasetUuid: string | null }>();

/**
 * 이 Revision 의 편집 상태를 얻는다. 같은 Revision 이면 쓰던 것을 주고, 다르면 새로 만들어 저장본을 받는다.
 * episode 를 바꿔도 같은 Revision 이면 편집본이 남는다.
 */
export function acquireMappingEditor(
  revisionUuid: string | null,
  datasetUuid: string | null = null,
): MappingEditor {
  const existing = editors.get(revisionUuid);
  if (existing !== undefined) return existing.editor;
  const editor = new MappingEditor(revisionUuid);
  editors.set(revisionUuid, { editor, datasetUuid });
  // 앱 셸의 layout effect가 구독을 시작한 뒤 초기 GET을 결정한다.
  queueMicrotask(() => editor.init(datasetUuid));
  return editor;
}

/**
 * 이 Revision의 편집 상태에 버리면 잃는 편집이 있는지 답한다.
 */
export function hasUnsavedMappingEdits(revisionUuid: string | null): boolean {
  const entry = editors.get(revisionUuid);
  return (
    revisionUuid !== null && entry !== undefined && hasUnsavedEdits(entry.editor.getSnapshot())
  );
}

/** 이 Dataset 의 매핑 편집 상태 가운데 버리면 잃는 편집이 있는 것이 있는지 답한다. */
export function hasUnsavedDatasetMappingEdits(datasetUuid: string): boolean {
  return [...editors.values()].some(
    (entry) => entry.datasetUuid === datasetUuid && hasUnsavedEdits(entry.editor.getSnapshot()),
  );
}

/** 이 Dataset 의 편집 상태를 버린다. 인자가 없으면 모두 버린다(세션 끝 · 테스트). */
export function disposeMappingEditors(datasetUuid?: string): void {
  for (const [key, entry] of editors) {
    if (datasetUuid !== undefined && entry.datasetUuid !== datasetUuid) continue;
    entry.editor.dispose();
    editors.delete(key);
  }
}
